<template>
  <el-form 
    :model="loginForm" 
    :rules="loginRules" 
    ref="loginFormRef" 
    label-width="80px"
    class="login-form"
    :class="{ 'is-mobile': isMobile }"
  >
  <el-form-item label="学号" prop="studentId">
      <el-input 
        v-model="loginForm.studentId" 
        placeholder="请输入学号"
        :size="isMobile ? 'large' : 'default'"
        :type="showPassword ? 'text' : 'password'"
      >
        <template #suffix>
          <el-icon 
            class="password-toggle" 
            @click="togglePassword"
            style="cursor: pointer;"
          >
            <View v-if="showPassword" />
            <Hide v-else />
          </el-icon>
        </template>
      </el-input>
    </el-form-item>
    <el-form-item label="姓名" prop="username">
      <el-input 
        v-model="loginForm.username" 
        placeholder="请输入姓名"
        :size="isMobile ? 'large' : 'default'"
      ></el-input>
    </el-form-item>
   
 
    <el-form-item class="login-button-item">
      <div class="button-group">
        <el-button 
          type="primary" 
          @click="submitForm" 
          :loading="loading"
          :size="isMobile ? 'large' : 'default'"
          class="login-button"
        >
          登录
        </el-button>
        <el-button 
          v-if="showRegisterButton"
          type="danger" 
          @click="handleRegister" 
          :loading="registerLoading"
          :size="isMobile ? 'large' : 'default'"
          class="register-button"
        >
          注册
        </el-button>
      </div>
    </el-form-item>
  </el-form>
</template>

<script>
import { reactive, ref, onMounted, onUnmounted, computed } from 'vue';
import { useStore } from 'vuex';
import authApi from '../api/auth';
import { ElMessage } from 'element-plus';
import { View, Hide } from '@element-plus/icons-vue';
import axios from 'axios';

export default {
    name: 'LoginDialog',
    components: {
        View,
        Hide
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true
        }
    },
    emits: ['update:modelValue', 'login-success'],
    setup(props, { emit }) {
        const store = useStore();
        const loginFormRef = ref(null);
        const loading = ref(false);
        const registerLoading = ref(false);
        const isMobile = ref(false);
        const showRegisterButton = ref(false);
        const showPassword = ref(false);

        const loginForm = reactive({
            studentId: '',
            username: '',
        });

        const loginRules = reactive({
            studentId: [
                { required: true, message: '请输入学号', trigger: 'blur' },
                { pattern: /^\d{1,20}$/, message: '学号是1-20位数字', trigger: 'blur' }
            ],
            username: [
                { required: true, message: '请输入姓名', trigger: 'blur' },
            ],
        });

        const visible = computed({
            get: () => props.modelValue,
            set: (value) => emit('update:modelValue', value)
        });

        // 检查是否为移动端
        const checkIsMobile = () => {
            isMobile.value = window.innerWidth < 768;
        };

        onMounted(() => {
            checkIsMobile();
            window.addEventListener('resize', checkIsMobile);
            const registerValue = localStorage.getItem('register');
            showRegisterButton.value = registerValue === '1';
        });

        onUnmounted(() => {
            window.removeEventListener('resize', checkIsMobile);
        });

        const submitForm = async () => {
            loginFormRef.value.validate(async (valid) => {
                if (valid) {
                    loading.value = true;
                    try {
                        const response = await authApi.login(loginForm.studentId, loginForm.username);
                        if (response.code === '000000') {
                            // 更新 store - 使用完整的 response.data
                            store.dispatch('login', response.data);
                            
                            // 将完整的用户信息存入 localStorage
                            localStorage.setItem('user', JSON.stringify(response.data));

                            ElMessage.success('登录成功');
                            // 传递用户信息给父组件
                            emit('close');
                            emit('login-success', response.data); // 传递完整的 data
                            visible.value = false;
                            
                            // 清空表单
                            loginForm.studentId = '';
                            loginForm.username = '';
                        } else {
                            ElMessage.error(response.msg || '登录失败');
                        }
                    } catch (error) {
                        console.error('登录错误:', error);
                        ElMessage.error('登录失败: ' + (error.response?.data?.msg || error.message));
                    } finally {
                        loading.value = false;
                    }
                }
            });
        };

        const handleRegister = async () => {
            loginFormRef.value.validate(async (valid) => {
                if (valid) {
                    registerLoading.value = true;
                    try {
                        const response = await axios.post('/api/user/add', {
                            studentId: loginForm.studentId,
                            username: loginForm.username
                        });
                        
                        if (response.data.code === '000000') {
                            ElMessage.success('注册成功，请登录');
                        } else {
                            ElMessage.error(response.data.msg || '注册失败');
                        }
                    } catch (error) {
                        console.error('注册错误:', error);
                        ElMessage.error('注册失败: ' + (error.response?.data?.msg || error.message));
                    } finally {
                        registerLoading.value = false;
                    }
                }
            });
        };

        const handleClose = () => {
            loginForm.studentId = '';
            loginForm.username = '';
            visible.value = false;
        };

        const togglePassword = () => {
            showPassword.value = !showPassword.value;
        };

        return {
            loginFormRef,
            loginForm,
            loginRules,
            loading,
            registerLoading,
            isMobile,
            submitForm,
            handleRegister,
            visible,
            handleClose,
            showRegisterButton,
            showPassword,
            togglePassword
        };
    },
};
</script>

<style scoped>
.login-form {
    padding: 20px;
    max-width: 400px;
    margin: 0 auto;
}

.login-button-item {
    margin-top: 30px;
    text-align: center;
}

.button-group {
    display: flex;
    gap: 10px;
    justify-content: center;
}

.login-button,
.register-button {
    flex: 1;
    max-width: 200px;
}

/* 移动端适配 */
@media (max-width: 768px) {
    .login-form {
        padding: 15px;
    }

    .login-form.is-mobile {
        padding: 10px;
    }

    .login-form.is-mobile :deep(.el-form-item__label) {
        padding-bottom: 8px;
    }

    .login-form.is-mobile :deep(.el-input__wrapper) {
        padding: 0 12px;
    }

    .button-group {
        flex-direction: column;
        width: 100%;
    }

    .login-button,
    .register-button {
        max-width: 100%;
        height: 44px;
        font-size: 16px;
    }

    .login-button-item {
        margin-top: 20px;
    }
}

/* 确保在移动端下输入框更容易点击 */
@media (max-width: 768px) {
    .login-form :deep(.el-input__wrapper) {
        min-height: 44px;
    }

    .login-form :deep(.el-form-item__content) {
        line-height: 44px;
    }
}

.password-toggle {
    color: var(--el-text-color-secondary);
    transition: color 0.3s ease;
}

.password-toggle:hover {
    color: var(--el-color-primary);
}
</style> 